import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { ApiError, authApi } from "../shared/api.ts";
import { useAuth } from "../shared/auth-context.tsx";
import { AuthLayout, AuthStatusMark } from "../components/AuthLayout.tsx";

/**
 * /auth/callback?token=... -- the magic-link landing page.
 *
 * Sign-in links are single-use: the first POST to /api/auth/verify spends
 * the link. Mail security scanners (Defender Safe Links, Mimecast,
 * Proofpoint) and link-preview clients open the URL in a headless browser and
 * run this page's JavaScript -- but they do not click. So this page NEVER
 * verifies on mount: it shows a "Continue signing in" button and POSTs only
 * when a person presses it (batch-4 F2 review I1).
 *
 * The token is read once, kept only in memory, and stripped from the address
 * bar at mount (history.replaceState), so it does not linger in history, the
 * Referer of anything this page loads, or a screenshot. `?next=` stays.
 *
 * A retryable failure (the store is briefly unavailable, or the network
 * dropped) keeps the in-memory token and offers "Try again"; a spent,
 * superseded or expired link offers "Request a new link". Never
 * auto-redirect to sign-in on error -- the cause would be invisible. Focus
 * moves to the error heading so a screen reader announces it.
 */
type Status = "ready" | "verifying" | "success" | "error";

function readToken(searchParams: URLSearchParams): string | null {
  const token = searchParams.get("token");
  return token && token.trim() ? token : null;
}

function stripTokenFromAddressBar() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("token")) return;
    url.searchParams.delete("token");
    // Keep the router's own history entry state; only the visible URL changes.
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  } catch {
    // A sandboxed or exotic history implementation: the token still works
    // from memory, it just stays visible.
  }
}

export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();

  // Read once. Held in memory only; the URL copy is removed below.
  const [token] = useState<string | null>(() => readToken(searchParams));
  const [next] = useState<string | null>(() => searchParams.get("next"));
  const [status, setStatus] = useState<Status>(token ? "ready" : "error");
  const [error, setError] = useState<string | null>(
    token ? null : "This link is missing the verification token.",
  );
  const [retryable, setRetryable] = useState(false);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const errorHeading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    mounted.current = true;
    stripTokenFromAddressBar();
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (status === "error") errorHeading.current?.focus();
  }, [status]);

  async function continueSigningIn() {
    if (!token || inFlight.current) return;
    inFlight.current = true;
    setStatus("verifying");
    setError(null);
    setRetryable(false);
    try {
      const r = await authApi.verify(token);
      if (!mounted.current) return;
      if (r.ok && r.email && r.tenant_id) {
        setSession({
          email: r.email,
          tenantId: r.tenant_id,
          signedInAt: Date.now(),
        });
        setStatus("success");
        // Validate next is a same-app path (don't open-redirect to external)
        const safeNext =
          next && next.startsWith("/") && !next.startsWith("//")
            ? next
            : "/playground";
        setTimeout(() => navigate(safeNext, { replace: true }), 400);
      } else {
        setStatus("error");
        setError(r.error || "Verification failed. Request a new link.");
      }
    } catch (err) {
      if (!mounted.current) return;
      // 503 = the link was NOT spent (the server failed closed before
      // consuming it); no status = the request never completed. Both are
      // worth one more press with the same in-memory token.
      const canRetry = !(err instanceof ApiError) || err.status === 503;
      setRetryable(canRetry);
      setStatus("error");
      setError(
        err instanceof Error && err.message
          ? err.message
          : "Network error verifying your link. Try again.",
      );
    } finally {
      inFlight.current = false;
    }
  }

  const pending = status === "verifying";

  return (
    <AuthLayout centered>
      {(status === "ready" || status === "verifying") && (
        <>
          <AuthStatusMark name="spark" />
          <p className="app-eyebrow">
            <span className="dot" /> Your sign-in link
          </p>
          <h1 className="app-h1">
            Continue <em>signing in</em>.
          </h1>
          <p className="app-lede" style={{ margin: "0 auto 28px" }}>
            {pending
              ? "Checking your link. This usually takes under a second."
              : "Press the button to finish signing in on this device."}
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={continueSigningIn}
            disabled={pending}
            aria-busy={pending}
            data-auth-continue
          >
            {pending ? "Signing you in…" : "Continue signing in →"}
          </button>
          <p className="sr-only" role="status" aria-live="polite">
            {pending ? "Signing you in" : ""}
          </p>
        </>
      )}
      {status === "success" && (
        <>
          <AuthStatusMark name="check" />
          <p className="app-eyebrow">
            <span className="dot" /> You're in
          </p>
          <h1 className="app-h1">
            Welcome <em>back</em>.
          </h1>
          <p className="app-lede" style={{ margin: "0 auto" }}>
            Loading your workspace…
          </p>
        </>
      )}
      {status === "error" && (
        <>
          <AuthStatusMark name="alert" tone="warn" />
          <p className="app-eyebrow">
            <span className="dot" style={{ background: "var(--rose)" }} />{" "}
            Link couldn't be verified
          </p>
          <h1
            className="app-h1"
            ref={errorHeading}
            tabIndex={-1}
            aria-describedby="auth-callback-error"
            data-auth-error
          >
            Something <em>didn't match</em>.
          </h1>
          <p id="auth-callback-error" className="app-lede" style={{ margin: "0 auto 28px" }}>
            {error}
          </p>
          {retryable && token ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={continueSigningIn}
              data-auth-retry
            >
              Try again →
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate("/signin", { replace: true })}
            >
              Request a new link →
            </button>
          )}
        </>
      )}
    </AuthLayout>
  );
}
